import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { API, useLanguage } from "../App";
import { Button } from "../components/ui/button";
import { 
  CheckCircle, 
  Loader2, 
  AlertCircle,
  Calendar,
  Clock,
  CreditCard,
  ArrowRight
} from "lucide-react";

const PaymentSuccessPage = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  
  const sessionId = searchParams.get("session_id");
  const paymentId = searchParams.get("payment_id");
  
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  const maxPollAttempts = 5;

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    
    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`);
      const data = response.data;
      
      setPaymentDetails(data);
      
      if (data.payment_status === "paid") {
        // Payment successful, create the appointment
        await createAppointment(data.payment_id);
      } else if (data.status === "expired") {
        setStatus("error");
        toast.error(language === "it" ? "Sessione di pagamento scaduta" : "Payment session expired");
      } else {
        // Payment still pending, continue polling
        if (pollAttempts < maxPollAttempts) {
          setPollAttempts(prev => prev + 1);
          setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus("error");
          toast.error(language === "it" ? "Timeout verifica pagamento" : "Payment verification timeout");
        }
      }
    } catch (error) {
      console.error("Error checking payment status:", error);
      if (pollAttempts < maxPollAttempts) {
        setPollAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus("error");
      }
    }
  };

  const createAppointment = async (payId) => {
    try {
      const response = await axios.post(`${API}/public/salon/${slug}/book-with-payment/${payId}`);
      
      if (response.data.success) {
        setAppointmentDetails(response.data);
        setStatus("success");
        toast.success(language === "it" ? "Prenotazione confermata!" : "Booking confirmed!");
      } else {
        setStatus("error");
      }
    } catch (error) {
      console.error("Error creating appointment:", error);
      // If appointment already exists, still show success
      if (error.response?.data?.detail?.includes("già") || error.response?.data?.already_exists) {
        setStatus("success");
      } else {
        setStatus("error");
      }
    }
  };

  if (status === "verifying") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-violet-500 animate-spin" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {language === "it" ? "Verifica pagamento in corso..." : "Verifying payment..."}
          </h1>
          <p className="text-slate-500 dark:text-slate-400">
            {language === "it" 
              ? "Attendere prego, stiamo confermando il tuo pagamento."
              : "Please wait, we're confirming your payment."
            }
          </p>
          <div className="mt-6 flex justify-center gap-1">
            {[...Array(5)].map((_, i) => (
              <div 
                key={i} 
                className={`w-2 h-2 rounded-full ${i < pollAttempts ? 'bg-violet-500' : 'bg-slate-200 dark:bg-slate-700'}`}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-red-500" />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
            {language === "it" ? "Errore nel pagamento" : "Payment Error"}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mb-8">
            {language === "it" 
              ? "Si è verificato un errore durante la verifica del pagamento. Riprova."
              : "An error occurred while verifying your payment. Please try again."
            }
          </p>
          
          <Button 
            className="w-full rounded-full py-6 text-lg font-semibold bg-gradient-to-r from-violet-500 to-purple-600"
            onClick={() => navigate(`/book/${slug}`)}
          >
            {language === "it" ? "Torna alla prenotazione" : "Back to booking"}
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-3xl p-8 shadow-2xl text-center animate-fade-in">
        <div className="w-20 h-20 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          {language === "it" ? "Pagamento Completato!" : "Payment Complete!"}
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mb-8">
          {language === "it" 
            ? "La tua prenotazione è stata confermata e il pagamento è stato elaborato con successo."
            : "Your booking has been confirmed and payment processed successfully."
          }
        </p>
        
        {/* Payment & Appointment Details */}
        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-2xl p-5 text-left space-y-4 mb-8">
          {paymentDetails?.appointment_details && (
            <>
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-600">
                <Calendar className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{language === "it" ? "Data" : "Date"}</p>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    {paymentDetails.appointment_details.date}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3 pb-3 border-b border-slate-200 dark:border-slate-600">
                <Clock className="w-5 h-5 text-emerald-500" />
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{language === "it" ? "Ora" : "Time"}</p>
                  <p className="font-semibold text-slate-900 dark:text-white">
                    {paymentDetails.appointment_details.time}
                  </p>
                </div>
              </div>
            </>
          )}
          
          <div className="flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-emerald-500" />
            <div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{language === "it" ? "Pagato" : "Paid"}</p>
              <p className="font-semibold text-emerald-600">
                €{paymentDetails?.amount?.toFixed(2) || "0.00"}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button 
            className="w-full rounded-full py-6 text-lg font-semibold bg-gradient-to-r from-emerald-500 to-teal-500"
            onClick={() => navigate(`/book/${slug}`)}
          >
            <span className="flex items-center gap-2">
              {language === "it" ? "Nuova Prenotazione" : "New Booking"}
              <ArrowRight className="w-5 h-5" />
            </span>
          </Button>
          
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {language === "it" 
              ? "Riceverai un'email di conferma con tutti i dettagli."
              : "You'll receive a confirmation email with all details."
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;
